import {
  Component,
  computed,
  EventEmitter,
  input,
  Input,
  model,
  Output
} from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { DxPopupTypes } from "devextreme-angular/ui/popup";
import { ValueChangedEvent } from "devextreme/ui/date_range_box";
import dxPopup from "devextreme/ui/popup";
import { isDate, isNull } from "lodash";
import { listify, title } from "radash";
import { dateUtil } from "src/app/support/date-util";
import { Bind } from "src/app/support/decorators/bind";
import { sntTranslate } from "src/app/support/translate";

@Component({
  standalone: false,
  selector: "snt-daterange-picker",
  templateUrl: "daterange-picker.component.html",
  styles: `
    :host {
      display: flex;
    }
  `
})
export class DaterangePickerComponent {
  @Input()
  mode: "dx" | "custom" = "custom";

  @Input()
  startDatePlaceholder: string | undefined;

  @Input()
  endDatePlaceholder: string | undefined;

  @Input()
  placeholder: string | undefined;

  presetMode = input<"default" | "quarters" | "semi-annual">("default");

  customPresets = input<Record<string, [Date, Date]> | undefined>(undefined);

  dxCustomPresets = computed<Array<{ text: string; value: [Date, Date] }>>(
    () => {
      const customPresets: Record<string, [Date, Date]> | undefined =
        this.customPresets();

      if (!customPresets) {
        return [];
      }

      return listify(customPresets, (key, value) => {
        return {
          text: key,
          value
        };
      });
    }
  );

  @Input()
  disableCustomPresets: boolean = false;

  @Input()
  disableResetButton: boolean = false;

  @Input()
  from: string | undefined;

  @Input()
  to: string | undefined;

  value = model<[Date | null, Date | null]>([null, null]);

  @Output()
  select: EventEmitter<[Date, Date]> = new EventEmitter<[Date, Date]>();

  @Output()
  clear: EventEmitter<any> = new EventEmitter<any>();

  @Bind
  onDxDateRangePopupInitialized(dxEvent: DxPopupTypes.InitializedEvent): void {
    if (this.dxCustomPresets().length > 0) {
      dxEvent.component?.option(
        "toolbarItems",
        this.#buildPresetsCustom({ dxPopup: dxEvent.component })
      );

      return;
    }

    switch (this.presetMode()) {
      case "default": {
        dxEvent.component?.option(
          "toolbarItems",
          this.#buildPresetsDefault({ dxPopup: dxEvent.component })
        );
        break;
      }
      case "quarters": {
        dxEvent.component?.option(
          "toolbarItems",
          this.#buildPresetsQuarter({ dxPopup: dxEvent.component })
        );
        break;
      }
      case "semi-annual": {
        dxEvent.component?.option(
          "toolbarItems",
          this.#buildPresetsSemiAnnual({ dxPopup: dxEvent.component })
        );
        break;
      }
    }
  }

  #buildPresetsCustom({
    dxPopup
  }: {
    dxPopup: dxPopup;
  }): DxPopupTypes.ToolbarItem[] {
    const that = this;

    return this.dxCustomPresets().map((preset) => ({
      location: "before" as const,
      toolbar: "bottom" as const,
      widget: "dxButton" as const,
      options: {
        text: title(preset.text),
        onClick: () => {
          const range: [Date, Date] = [
            preset.value[0],
            dateUtil(preset.value[1]).endOf("day").toDate()
          ];
          that.value.set(range);
          that.select.emit(range);
          dxPopup?.hide();
        }
      }
    }));
  }

  #buildPresetsSemiAnnual({
    dxPopup
  }: {
    dxPopup: dxPopup;
  }): DxPopupTypes.ToolbarItem[] {
    const toolbarItems: DxPopupTypes.ToolbarItem[] = [];
    const that = this;

    for (let i = 0; i < 2; i++) {
      const from = dateUtil()
        .startOf("year")
        .add(i * 6, "month")
        .startOf("month");

      const to = dateUtil()
        .startOf("year")
        .add(i * 6 + 5, "month")
        .endOf("month");

      toolbarItems.push({
        location: "before",
        toolbar: "bottom",
        widget: "dxButton",
        options: {
          text: title(
            sntTranslate(marker(`${i === 0 ? "Primo" : "Secondo"} semestre`))
          ),
          onClick: () => {
            that.value.set([from.toDate(), to.toDate()]);
            dxPopup?.hide();
          }
        }
      });
    }

    for (let i = 0; i < 2; i++) {
      const from = dateUtil()
        .startOf("year")
        .add(-1 * i, "year")
        .startOf("month");

      const to = dateUtil()
        .startOf("year")
        .add(-1 * i, "year")
        .add(11, "month")
        .endOf("month");

      toolbarItems.push({
        location: "before",
        toolbar: "bottom",
        widget: "dxButton",
        options: {
          text: title(
            sntTranslate(marker(`Anno ${i === 0 ? "corrente" : "precedente"}`))
          ),
          onClick: () => {
            that.value.set([from.toDate(), to.toDate()]);
            dxPopup?.hide();
          }
        }
      });
    }

    return toolbarItems;
  }

  #buildPresetsQuarter({
    dxPopup
  }: {
    dxPopup: dxPopup;
  }): DxPopupTypes.ToolbarItem[] {
    const toolbarItems: DxPopupTypes.ToolbarItem[] = [];
    const that = this;

    const months = dateUtil().month() + 1;
    const quartesAvailable = Math.floor(months / 3);

    for (let i = 0; i < quartesAvailable; i++) {
      const from = dateUtil()
        .startOf("year")
        .add(i * 3, "month")
        .startOf("month");

      const to = dateUtil()
        .startOf("year")
        .add(i * 3 + 2, "month")
        .endOf("month");

      toolbarItems.push({
        location: "before",
        toolbar: "bottom",
        widget: "dxButton",
        options: {
          text: title(`Q${i + 1} ${from.get("year").toString()}`),
          onClick: () => {
            that.value.set([from.toDate(), to.toDate()]);
            dxPopup?.hide();
          }
        }
      });
    }

    for (let i = 3; i >= 0; i--) {
      const from = dateUtil()
        .add(-1, "year")
        .startOf("year")
        .add(i * 3, "month")
        .startOf("month");

      const to = dateUtil()
        .add(-1, "year")
        .startOf("year")
        .add(i * 3 + 2, "month")
        .endOf("month");

      toolbarItems.push({
        location: "before",
        toolbar: "bottom",
        widget: "dxButton",
        options: {
          text: title(`Q${i + 1} ${from.get("year").toString()}`),
          onClick: () => {
            that.value.set([from.toDate(), to.toDate()]);
            dxPopup?.hide();
          }
        }
      });
    }

    return toolbarItems;
  }

  #buildPresetsDefault({
    dxPopup
  }: {
    dxPopup: dxPopup;
  }): DxPopupTypes.ToolbarItem[] {
    const toolbarItems: DxPopupTypes.ToolbarItem[] = [];
    const that = this;

    const currentMonthFrom = dateUtil().startOf("month");
    const currentMonthTo = dateUtil();

    toolbarItems.push({
      location: "before",
      toolbar: "bottom",
      widget: "dxButton",
      options: {
        text: title(currentMonthTo.format("MMMM")),
        onClick: () => {
          that.value.set([currentMonthFrom.toDate(), currentMonthTo.toDate()]);
          dxPopup?.hide();
        }
      }
    });

    const last3MonthsFrom = dateUtil().add(-4, "month").startOf("month");
    const last3MonthsTo = dateUtil().add(-1, "month").endOf("month");

    toolbarItems.push({
      location: "before",
      toolbar: "bottom",
      widget: "dxButton",
      options: {
        text: `${title(last3MonthsFrom.format("MMM"))} - ${title(last3MonthsTo.format("MMM"))}`,
        onClick: () => {
          that.value.set([last3MonthsFrom.toDate(), last3MonthsTo.toDate()]);
          dxPopup?.hide();
        }
      }
    });

    const currentYearFrom = dateUtil().startOf("year");
    const currentYearTo = dateUtil();

    toolbarItems.push({
      location: "before",
      toolbar: "bottom",
      widget: "dxButton",
      options: {
        text: title(currentYearTo.format("YYYY")),
        onClick: () => {
          that.value.set([currentYearFrom.toDate(), currentYearTo.toDate()]);
          dxPopup?.hide();
        }
      }
    });

    const pastYearFrom = dateUtil().add(-1, "year").startOf("year");
    const pastYearTo = dateUtil().add(-1, "year").endOf("year");

    toolbarItems.push({
      location: "before",
      toolbar: "bottom",
      widget: "dxButton",
      locateInMenu: "always",
      options: {
        text: title(pastYearTo.format("YYYY")),
        onClick: () => {
          that.value.set([pastYearFrom.toDate(), pastYearTo.toDate()]);
          dxPopup?.hide();
        }
      }
    });

    const past3YearsFrom = dateUtil().add(-4, "year").startOf("year");
    const past3YearsTo = dateUtil().add(-1, "year").endOf("year");

    toolbarItems.push({
      location: "before",
      toolbar: "bottom",
      widget: "dxButton",
      locateInMenu: "always",
      options: {
        text: `${title(past3YearsFrom.format("YYYY"))} - ${title(past3YearsTo.format("YYYY"))}`,
        onClick: () => {
          that.value.set([past3YearsFrom.toDate(), past3YearsTo.toDate()]);
          dxPopup?.hide();
        }
      }
    });

    return toolbarItems;
  }

  onDxDateRangeSelected(dxEvent: ValueChangedEvent): void {
    if (dxEvent.value && dxEvent.value.length === 2) {
      if (isNull(dxEvent.value[0]) && isNull(dxEvent.value[1])) {
        this.clear.emit();
        return;
      }

      if (isDate(dxEvent.value[0]) && isDate(dxEvent.value[1])) {
        this.select.emit([
          dxEvent.value[0],
          dateUtil(dxEvent.value[1]).endOf("day").toDate()
        ]);
        return;
      }
    }
  }
}
